import get from 'Utils/lodash.get';
import set from 'Utils/lodash.set';
import isEqual from 'Utils/lodash.isequal';
import merge from 'Utils/lodash.merge';

export { get, set, isEqual, merge };

export function isNil(value) {
  return value === undefined || value === null;
}

export function isEmptyValue(value) {
  return isNil(value) || value === '';
}

export function isString(value) {
  return typeof value === 'string' || value instanceof String;
}

export function isFunction(value) {
  return typeof value === 'function';
}

export function isArray(value) {
  return Array.isArray(value);
}

export function isEmptyArray(array) {
  return isArray(array) && !array.length;
}

export function isObject(value) {
  return typeof value === 'object' && value !== null && !isArray(value);
}

export function isNotEmptyObject(value) {
  return isObject(value) && Boolean(Object.keys(value).length);
}

export function upperFirst(string) {
  if (!isString(string)) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function startCase(string) {
  if (!isString(string)) return string;
  return string
    .replace(/[-_]/g, ' ')
    .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

export function isObjectHasTruthlyValues(value) {
  return isObject(value) && Boolean(Object.values(value).some(v => v));
}

export function cloneDeep(value) {
  if (!isObject(value) && !isArray(value)) return value;
  return JSON.parse(JSON.stringify(value));
}

export function getLastArrayElement(arr) {
  if (isArray(arr) && !arr.length) throw new Error('getLastArrayElement expect array as argument');
  return arr[arr.length - 1];
}

export function getRandomArrayElements(arr, n = 1) {
  const num = n > arr.length ? arr.length : n;
  const shuffled = arr.slice(0).sort(() => 0.5 - Math.random());
  return shuffled.slice(0, num);
}

/**
 *
 * @param baseArray {array}
 * @param values {array}
 * @returns {Boolean}
 */
export function getIsArrayIncludesEvery(baseArray, values) {
  return values.every(value => baseArray.includes(value));
}

export function sortBy(arr, param) {
  arr.sort((a, b) => {
    if (a[param] < b[param]) return -1;
    if (a[param] > b[param]) return 1;
    return 0;
  });
  return arr;
}

/**
 * Deep clear object of empty values
 * @param value
 * @returns {*}
 */
export function clearObject(value) {
  if (!isObject(value)) return value;
  const obj = {};
  Object.entries(value).forEach(([key, val]) => {
    if (!isEmptyValue(val) && (!isObject(val) || isNotEmptyObject(val))) {
      obj[key] = isObject(val) ? clearObject(val) : val;
    }
  });
  return obj;
}

export function pickObject(obj, keys) {
  const newObj = {};
  keys.forEach((key) => {
    newObj[key] = obj[key];
  });
  return newObj;
}

export function pickObjectsInArray(arr, keys) {
  const newArr = [];
  arr.forEach(obj => newArr.push(
    pickObject(obj, keys),
  ));
  return newArr;
}

/**
 * Get source object and filter object
 * return new object base on source, but without properties which exist in filter object
 * handle nested objects
 * @param sourceObj {object}
 * @param filterObj {object}
 */
export function omitObjectKeysByObject(sourceObj, filterObj) {
  if (!isObject(sourceObj) || !isObject(filterObj)) return sourceObj;
  const result = {};
  Object.entries(sourceObj).forEach(([key, value]) => {
    // handle nested
    if (isObject(value)) result[key] = omitObjectKeysByObject(value, filterObj[key]);
    else if (filterObj[key] === undefined) result[key] = value;
  });
  return clearObject(result);
}

export function omitObjectValuesByObject(sourceObj, filterObj) {
  if (!isObject(sourceObj) || !isObject(filterObj)) return sourceObj;
  const result = {};
  Object.entries(sourceObj).forEach(([key, value]) => {
    const filterValue = filterObj[key];
    // handle nested
    if (isObject(value)) result[key] = omitObjectValuesByObject(value, filterValue);
    else if (!isEqual(value, filterValue)) result[key] = value;
  });
  return clearObject(result);
}

// Generate id from random numbers and letters
export function generateIdShort() {
  return Math.random().toString(36).slice(2);
}

export function generateId() {
  return generateIdShort() + generateIdShort();
}

export function wait(timeout = 0) {
  return new Promise(((resolve) => {
    setTimeout(resolve, timeout);
  }));
}

export function debounce(fn, delay) {
  let timeoutID = null;
  return (...args) => {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
